import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Swal from '../../theme/swal';
import { IcArrowLeft, IcUser, IcMail, IcLock, IcEye, IcEyeOff, IcSave, IcShieldCheck, IcUserCheck, IcReceiptText, IcUserPlus, IcAlertCircle, IcKeyRound, IcBuilding } from '../../theme/icons';
import authService from '../../services/authService';
import storeService from '../../services/storeService';

// Formulaire (Brand Kit §05) : champ .inp 42px bordé #D1D5DB rayon 6,
// 13.5px, focus 2px encre (icône à gauche → pl-11) ; libellé 12px 500 encre ;
// erreur = .inp-err (bordure 2px rouge, fond #FEF2F2) + message .form-err.
const inputClass = 'inp w-full pl-11 pr-3 py-2.5 rounded-xl text-base text-gray-900';
const inputErrClass = 'inp inp-err w-full pl-11 pr-3 py-2.5 rounded-xl text-base text-gray-900';
const labelClass = 'block text-sm font-medium text-gray-900 mb-1.5';

// Mode panneau (owner 2026-09-08) : rendu dans une modale de la liste
// (Paramètres › Utilisateurs) → `userId` par prop, `onDone` / `onCancel`
// remplacent la navigation, Retour + chip masqués. Sans props = page /users/add.
// `defaults` : valeurs de départ d'un nouveau compte (ex. { stores: [2] } depuis
// Paramètres › Boutiques).
const UserForm = ({ userId: userIdProp, onDone, onCancel, defaults } = {}) => {
  const params = useParams();
  const userId = userIdProp ?? params.userId;
  const navigate = useNavigate();
  const embedded = Boolean(onDone || onCancel);
  const isEditMode = Boolean(userId);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirm_password: '',
    role: 'seller',
    is_active: true,
    has_facturation_access: false,
    ...(defaults || {}),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [pin, setPin] = useState('');            // nouveau code à la caisse (manager / propriétaire)
  const [removePin, setRemovePin] = useState(false);
  // Boutique d'une NOUVELLE caisse (owner 2026-10-03) : obligatoire. Les
  // comptes existants se rangent dans Paramètres › Boutiques.
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(() => (defaults && defaults.stores && defaults.stores[0]) || null);
  const [storeError, setStoreError] = useState(false);
  useEffect(() => {
    if (isEditMode) return;
    storeService.list().then((list) => {
      const active = list.filter((st) => st.is_active !== false);
      setStores(active);
      if (active.length === 1) setStoreId((cur) => cur || active[0].id);   // une seule boutique : déjà choisie
    }).catch(() => setStores([]));
  }, [isEditMode]);
  useEffect(() => {
    if (!authService.isOwner()) {
      Swal.fire('Accès refusé', "Vous n'avez pas la permission d'accéder à cette page.", 'error');
      navigate('/');
      return;
    }
    if (isEditMode) {
      (async () => {
        try {
          setLoading(true);
          const response = await authService.getUser(userId);
          const { password, ...userData } = response;
          setFormData((prev) => ({ ...prev, ...userData, password: '', confirm_password: '' }));
        } catch (err) {
          console.error('Error fetching user data:', err);
          setError("Impossible de charger l'utilisateur.");
        } finally {
          setLoading(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const pwdMismatch =
    (formData.password || formData.confirm_password) &&
    formData.password !== formData.confirm_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (pwdMismatch) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (!isEditMode && !formData.password) {
      setError('Le mot de passe est requis.');
      return;
    }
    if (formData.role !== 'seller' && pin && pin.length < 2) {
      setError('Le code à la caisse comporte au moins 2 caractères.');
      return;
    }
    if (!isEditMode && formData.role === 'seller' && !storeId) {
      setStoreError(true);
      setError('Choisissez la boutique de cette caisse.');
      return;
    }

    try {
      setLoading(true);
      const { confirm_password, ...baseData } = formData;
      let dataToSubmit = { ...baseData };
      if (isEditMode && !dataToSubmit.password) {
        const { password, ...rest } = dataToSubmit;
        dataToSubmit = rest;
      } else {
        dataToSubmit.confirm_password = formData.confirm_password;
      }
      // Sellers only carry the facturation flag.
      if (dataToSubmit.role !== 'seller') dataToSubmit.has_facturation_access = false;
      // Le propriétaire va partout : pas de boutique.
      // Code à la caisse (manager / propriétaire) : tapé = nouveau ; « Retirer » = ''.
      ['has_pin'].forEach((k) => delete dataToSubmit[k]);
      if (dataToSubmit.role !== 'seller') {
        if (pin) dataToSubmit.pin = pin;
        else if (removePin) dataToSubmit.pin = '';
      }
      // Les boutiques se rangent dans Paramètres › Boutiques, pas ici — sauf la
      // boutique OBLIGATOIRE d'une nouvelle caisse (owner 2026-10-03).
      delete dataToSubmit.stores;
      delete dataToSubmit.store;
      delete dataToSubmit.store_name;
      delete dataToSubmit.store_names;
      if (!isEditMode && formData.role === 'seller') dataToSubmit.stores = [storeId];

      if (isEditMode) {
        await authService.updateUser(userId, dataToSubmit);
      } else {
        await authService.createUser(dataToSubmit);
      }
      await Swal.fire({
        icon: 'success',
        title: isEditMode ? 'Utilisateur mis à jour' : 'Utilisateur créé',
        timer: 1500, showConfirmButton: false,
      });
      if (onDone) onDone(); else navigate('/users');
    } catch (err) {
      console.error('Error submitting form:', err);
      const data = err.response?.data || err;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        const msgs = [];
        for (const field in data) {
          const v = data[field];
          msgs.push(`${field}: ${Array.isArray(v) ? v.join(', ') : v}`);
        }
        setError(msgs.join('\n') || "Erreur lors de l'enregistrement.");
      } else {
        setError("Une erreur est survenue lors de l'enregistrement.");
      }
      setLoading(false);
    }
  };

  // Trois types de COMPTE : caisse, manager, propriétaire. Les personnes qui
  // s'en servent (vendeurs, managers) se gèrent à part : Paramètres › Personnes.
  const roleOptions = [
    { value: 'seller', label: 'Caisse', desc: 'Le compte ouvert en boutique, utilisé par ses vendeurs et managers', Icon: IcUserCheck, active: 'border-blue-500 bg-blue-50 text-blue-700' },
    { value: 'manager', label: 'Manager', desc: 'Tout, sauf gérer les comptes et les personnes', Icon: IcShieldCheck, active: 'border-green-600 bg-green-50 text-green-700' },
    { value: 'admin', label: 'Propriétaire', desc: 'Accès complet à tout, dans toutes les boutiques', Icon: IcShieldCheck, active: 'border-amber-500 bg-amber-50 text-amber-700' },
  ];

  return (
    <div className={embedded ? 'p-6' : 'w-full px-4 pt-2 pb-4'}>
      {!embedded && (
      <Link to="/users" className="btn-s px-5 py-2.5 text-sm mb-4 transition-colors">
        <IcArrowLeft className="mr-2" /> Retour
      </Link>
      )}

      {/* Page name as a gray chip — same design as the AddProduct page
          (owner Studio); old icon box + h1 removed. */}
      {!embedded && (
      <div className="mb-6">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border bg-gray-100 border-gray-400 text-gray-500 shadow-sm">
          {isEditMode ? <IcUser size={13} /> : <IcUserPlus size={13} />}
          {isEditMode ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}
        </span>
      </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-5 whitespace-pre-line" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className={embedded ? 'space-y-5' : 'bg-white rounded-2xl border border-gray-200 p-6 space-y-5'}>
        {/* Username */}
        <div>
          <label className={labelClass}>Nom d'utilisateur <span className="req">*</span></label>
          <div className="relative">
            <IcUser className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className={inputClass} type="text" name="username" value={formData.username} onChange={handleChange} placeholder="ex : ahmed" required />
          </div>
        </div>

        {/* Email */}
        <div>
          <label className={labelClass}>Email <span className="req">*</span></label>
          <div className="relative">
            <IcMail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input className={inputClass} type="email" name="email" value={formData.email} onChange={handleChange} placeholder="nom@kipara.store" required />
          </div>
        </div>

        {/* Password + confirm */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Mot de passe {!isEditMode && <span className="req">*</span>}{isEditMode && <span className="text-gray-400 font-normal">(laisser vide = inchangé)</span>}</label>
            <div className="relative">
              <IcLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className={`${inputClass} pr-10`} type={showPwd ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange} placeholder="••••••••" required={!isEditMode} autoComplete="new-password" />
              <button type="button" onClick={() => setShowPwd((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" tabIndex={-1}>
                {showPwd ? <IcEyeOff /> : <IcEye />}
              </button>
            </div>
          </div>
          <div>
            <label className={labelClass}>Confirmer {!isEditMode && <span className="req">*</span>}</label>
            <div className="relative">
              <IcLock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className={pwdMismatch ? inputErrClass : inputClass} type={showPwd ? 'text' : 'password'} name="confirm_password" value={formData.confirm_password} onChange={handleChange} placeholder="••••••••" required={!isEditMode} autoComplete="new-password" />
            </div>
            {pwdMismatch && <p className="form-err"><IcAlertCircle size={12} /> Les mots de passe ne correspondent pas.</p>}
          </div>
        </div>

        {/* Role picker */}
        <div>
          <label className={labelClass}>Rôle</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {roleOptions.map((r) => {
              const selected = formData.role === r.value;
              return (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, role: r.value }))}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 text-left transition ${selected ? r.active : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
                >
                  <r.Icon className={`mt-0.5 ${selected ? '' : 'text-gray-400'}`} />
                  <span>
                    <span className="block text-sm font-semibold">{r.label}</span>
                    <span className="block text-xs opacity-80">{r.desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Boutique — vendeur et manager (owner 2026-09-28) ; le propriétaire va partout.
            Nouvelle caisse (owner 2026-10-03) : choix obligatoire, même patron que le rôle. */}
        {formData.role === 'admin' ? (
          <p className="text-xs text-gray-400 -mt-2">Un compte propriétaire voit toutes les boutiques.</p>
        ) : (!isEditMode && formData.role === 'seller') ? (
          <div>
            <label className={labelClass} id="caisse-store-label">Boutique <span className="req">*</span></label>
            {stores.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" role="radiogroup" aria-labelledby="caisse-store-label">
                {stores.map((st) => {
                  const selected = storeId === st.id;
                  return (
                    <button
                      key={st.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => { setStoreId(st.id); setStoreError(false); setError(null); }}
                      className={`flex items-center gap-3 p-3 rounded-xl border-2 text-left text-sm font-semibold transition ${selected ? 'border-blue-500 bg-blue-50 text-blue-700' : storeError ? 'border-red-300 text-gray-600 hover:border-red-400' : 'border-gray-200 hover:border-gray-300 text-gray-600'}`}
                    >
                      <IcBuilding className={selected ? '' : 'text-gray-400'} /> {st.name}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Aucune boutique active — ajoutez-en une dans Paramètres › Boutiques avant de créer la caisse.</p>
            )}
            {storeError
              ? <p className="form-err"><IcAlertCircle size={12} /> Une caisse travaille obligatoirement dans une boutique.</p>
              : <p className="mt-1.5 text-xs text-gray-400">Une caisse travaille dans une seule boutique ; changement possible ensuite dans Paramètres › Boutiques.</p>}
          </div>
        ) : (
          <p className="text-xs text-gray-500 -mt-2">
            <span className="font-medium text-gray-700">Boutique : </span>
            {(formData.store_names || []).length ? formData.store_names.join(', ') : 'aucune pour l’instant'}
            <span className="text-gray-400"> — se range dans Paramètres › Boutiques.</span>
          </p>
        )}

        {/* Code du COMPTE à la caisse — manager et propriétaire (owner
            2026-09-30) : marche sur TOUTES les caisses (retours, liste des
            produits…). Sur son propre compte, rien n'est demandé. */}
        {formData.role !== 'seller' && (
          <div className="rounded-xl border border-gray-200 p-4">
            <p className="text-sm font-semibold text-gray-900">À la caisse</p>
            <p className="text-xs text-gray-400 mt-0.5 mb-3">Ce code ouvre les actions réservées sur toutes les caisses. Connecté à ce compte, rien n’est demandé.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className={labelClass} htmlFor="user-pin">Code {formData.has_pin && !removePin && <span className="text-gray-400 font-normal">(défini — vide = inchangé)</span>}</label>
                <div className="relative">
                  <IcKeyRound className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input id="user-pin" className={inputClass} type="text" maxLength={12} value={pin} placeholder="au moins 2 caractères"
                    onChange={(e) => { setPin(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 12)); setRemovePin(false); }}
                    autoComplete="off" autoCapitalize="off" spellCheck={false} />
                </div>
                {formData.has_pin && !pin && (
                  removePin
                    ? <p className="mt-1.5 text-xs text-red-600">Le code sera retiré à l’enregistrement. <button type="button" onClick={() => setRemovePin(false)} className="underline text-gray-600 hover:text-gray-900">Garder</button></p>
                    : <button type="button" onClick={() => setRemovePin(true)} className="mt-1.5 px-2 py-1 -ml-2 rounded-lg text-xs text-red-600 hover:bg-red-50">Retirer le code</button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Facturation access — sellers only */}
        {formData.role === 'seller' && (
          <label className="flex items-start gap-3 p-3 rounded-xl bg-amber-50 border border-amber-100 cursor-pointer">
            <input type="checkbox" name="has_facturation_access" checked={formData.has_facturation_access} onChange={handleChange} className="mt-1 h-4 w-4 accent-amber-600" />
            <span>
              <span className="flex items-center gap-2 text-sm font-semibold text-amber-800"><IcReceiptText /> Accès aux factures &amp; fournisseurs</span>
              <span className="block text-xs text-amber-700/80 mt-0.5">Si coché, cette caisse peut ouvrir les pages Facturation, Fournisseurs et Laboratoires (consultation).</span>
            </span>
          </label>
        )}

        {/* Active toggle */}
        <label className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-100 cursor-pointer">
          <span>
            <span className="block text-sm font-semibold text-gray-700">Compte actif</span>
            <span className="block text-xs text-gray-400">Un compte inactif ne peut pas se connecter.</span>
          </span>
          <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} className="h-5 w-5 accent-green-600" />
        </label>

        {/* Actions */}
        {/* Brand Kit §05 : pied de formulaire aligné à droite — filet gray-100,
            pt 12, gap 8 — Annuler ghost + Enregistrer encre */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-2">
          <button
            type="button"
            onClick={() => (onCancel ? onCancel() : navigate('/users'))}
            className="btn-g px-5 py-2.5 transition"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={loading || pwdMismatch}
            className="btn-p gap-2 px-5 py-2.5 transition"
          >
            <IcSave /> {loading ? 'Enregistrement…' : (isEditMode ? 'Enregistrer' : "Créer l'utilisateur")}
          </button>
        </div>
      </form>


    </div>
  );
};

export default UserForm;
